import React, { FC } from 'react';
import { GetAgentStatusResponse } from 'Rewards/services/endpoints/GetAgentStatus/types';
import {
  GetRewardsConfigResponse,
  maxWinPrizeForPhase,
} from 'Rewards/services/endpoints/GetRewardsConfig/types';
import { BonusEntriesBadge } from 'Rewards/components/BonusEntriesBadge';
import styles from './styles.module.css';

export type ScratchOffCardFullProps = {
  status: GetAgentStatusResponse | null;
  config: GetRewardsConfigResponse | null;
  /** Whether the POST /attempts call is in progress (disables the button while pending). */
  submitPending: boolean;
  /** Called when the producer clicks "Play Scratch-off". Not called when button is disabled. */
  onPlayScratchOff: () => void;
};

export const ScratchOffCardFull: FC<ScratchOffCardFullProps> = ({
  status,
  config,
  submitPending,
  onPlayScratchOff,
}) => {
  const currentPhaseCode = status?.phase?.code ?? config?.phase?.code ?? null;
  const maxPrize =
    config && currentPhaseCode
      ? maxWinPrizeForPhase(config, currentPhaseCode)
      : null;

  const regularEntries = status?.balance?.regular ?? 0;
  const bonusEntries = status?.balance?.bonus ?? 0;
  const totalEntries = regularEntries + bonusEntries;
  const canPlay = totalEntries > 0 && !submitPending;

  const policiesRemaining = status?.progress?.policiesUntilNextEntry ?? 0;
  const entryThreshold = config?.config?.program?.entry_threshold ?? null;
  const filledSegments =
    entryThreshold != null
      ? Math.max(0, entryThreshold - policiesRemaining)
      : 0;

  return (
    <div className={styles.scratchCard} data-testid="scratch-off-card-full">
      {/* Label + headline */}
      <p className={styles.cardLabel}>SCRATCH-OFF CARDS</p>
      {maxPrize != null && (
        <p
          className={styles.prizeHeadline}
          data-testid="scratch-off-full-headline"
        >
          Win up to ${maxPrize.toLocaleString('en-US')}
        </p>
      )}

      {/* Entry counts */}
      <div className={styles.entriesRow} data-testid="scratch-off-full-counts">
        <span
          className={styles.entriesCount}
          data-testid="scratch-off-full-regular-count"
        >
          {regularEntries} ENTRIES EARNED
        </span>
        <BonusEntriesBadge
          phaseNumber={status?.phase?.number ?? 1}
          bonusCount={bonusEntries}
        />
      </div>

      {/* Progress toward next entry */}
      {status?.progress && entryThreshold != null && (
        <div
          className={styles.progressSection}
          data-testid="scratch-off-full-progress"
        >
          <div className={styles.progressLabels}>
            <span>NEXT ENTRY</span>
            <span>
              {policiesRemaining} {policiesRemaining === 1 ? 'SALE' : 'SALES'}{' '}
              AWAY
            </span>
          </div>
          <div
            className={styles.progressBar}
            data-testid="scratch-off-full-progress-bar"
          >
            {Array.from({ length: entryThreshold }).map((_, i) => (
              <div
                key={i}
                className={
                  i < filledSegments
                    ? styles.progressSegmentFilled
                    : styles.progressSegmentEmpty
                }
              />
            ))}
          </div>
        </div>
      )}

      {/* Play Scratch-off button — disabled when no entries (AC2) */}
      <button
        className={styles.playButton}
        onClick={onPlayScratchOff}
        disabled={!canPlay}
        data-testid="scratch-off-play-button"
        aria-disabled={!canPlay}
      >
        {submitPending ? 'LOADING…' : 'Play Scratch-off'}
      </button>
    </div>
  );
};
